import { GRID_H, GRID_W, MOONLIGHT_START } from './constants.ts';
import {
  FailReason,
  Outcome,
  Phase,
  Tile,
  type RoomDef,
  type SimState,
  type TileKind,
} from './types.ts';

const CHAR_TO_TILE: Record<string, TileKind> = {
  '.': Tile.Empty,
  ' ': Tile.Empty,
  '#': Tile.Stone,
  '~': Tile.Vine,
};

export function idx(tx: number, ty: number): number {
  return ty * GRID_W + tx;
}

export function inBounds(tx: number, ty: number): boolean {
  return tx >= 0 && tx < GRID_W && ty >= 0 && ty < GRID_H;
}

/**
 * Out of bounds reads as Stone on the sides and top (the room is sealed) but as
 * Empty below the floor, so falling out of the world is a real failure state
 * rather than an invisible wall.
 */
export function tileAt(state: SimState, tx: number, ty: number): TileKind {
  if (tx < 0 || tx >= GRID_W || ty < 0) return Tile.Stone;
  if (ty >= GRID_H) return Tile.Empty;
  return (state.tiles[idx(tx, ty)] ?? Tile.Empty) as TileKind;
}

/** A tile blocks movement only when it is settled. Spawning and crumbling blocks
 *  are deliberately passable — advanced play casts through those windows. */
export function isSolid(state: SimState, tx: number, ty: number): boolean {
  if (tx < 0 || tx >= GRID_W || ty < 0) return true;
  if (ty >= GRID_H) return false;
  const i = idx(tx, ty);
  const kind = state.tiles[i] ?? Tile.Empty;
  if (kind === Tile.Empty) return false;
  return (state.phase[i] ?? Phase.Settled) === Phase.Settled;
}

export function createState(room: RoomDef): SimState {
  const tiles = new Uint8Array(GRID_W * GRID_H);
  const phase = new Uint8Array(GRID_W * GRID_H);
  const phaseTimer = new Float64Array(GRID_W * GRID_H);
  const hits = new Uint8Array(GRID_W * GRID_H);

  if (room.map.length !== GRID_H) {
    throw new Error(`Room ${room.id}: expected ${GRID_H} rows, got ${room.map.length}`);
  }
  for (let ty = 0; ty < GRID_H; ty++) {
    const row = room.map[ty] ?? '';
    if (row.length !== GRID_W) {
      throw new Error(`Room ${room.id}: row ${ty} is ${row.length} wide, expected ${GRID_W}`);
    }
    for (let tx = 0; tx < GRID_W; tx++) {
      const ch = row[tx] ?? '.';
      const kind = CHAR_TO_TILE[ch];
      if (kind === undefined) {
        throw new Error(`Room ${room.id}: unknown map character '${ch}' at ${tx},${ty}`);
      }
      tiles[idx(tx, ty)] = kind;
    }
  }

  let nextId = 1;

  return {
    tick: 0,
    tiles,
    phase,
    phaseTimer,
    hits,
    player: {
      x: room.spawn.x,
      y: room.spawn.y,
      vx: 0,
      vy: 0,
      facing: 1,
      grounded: false,
      coyote: 0,
      jumpBuffer: 0,
      castCooldown: 0,
      rangedCooldown: 0,
      prevButtons: 0,
      hasMoonseed: false,
      rangedCharges: 0,
    },
    chimera: room.chimera.map((c) => ({
      id: nextId++,
      kind: c.kind,
      x: c.x,
      y: c.y,
      vy: 0,
      dir: c.dir ?? 1,
      timer: 0,
      alive: true,
    })),
    items: room.items.map((it) => ({
      id: nextId++,
      kind: it.kind,
      tx: it.tx,
      ty: it.ty,
      hidden: it.hidden ?? false,
      taken: false,
    })),
    doors: (room.doors ?? []).map((d) => ({
      id: nextId++,
      tx: d.tx,
      ty: d.ty,
      targetRoomId: d.targetRoomId,
      locked: d.locked ?? false,
      label: d.label ?? '',
      hidden: d.hidden ?? false,
    })),
    doorPrompt: null,
    portalPrompt: false,
    portal: { tx: room.portal.tx, ty: room.portal.ty, hidden: room.portal.hidden ?? false },
    moonlight: room.moonlight ?? MOONLIGHT_START,
    outcome: Outcome.Playing,
    failReason: FailReason.None,
    nextId,
  };
}
